import { ObjectTools } from '../../../tools/object-tools';
import { SkeletonTools } from '../../../tools/skeleton-tools';
import { SolLoader } from '../../../models/core/sol-loader';

export interface SolLoaderRenderRow {
  rowClass: string;
  cells: SolLoaderRenderCell[];
}

export interface SolLoaderRenderCell {
  cellClass: string;
  opacity: number;
}

export class SolLoaderState {
  private static readonly frozenOpacity = 1;

  private constructor(
    public readonly loader: SolLoader,
    public readonly loading: boolean,
    public readonly waveIndex: number,
    public readonly hostClass: string,
    public readonly renderRows: SolLoaderRenderRow[],
  ) {}

  public static of(loader: SolLoader): SolLoaderState {
    return new SolLoaderState(loader, true, 0, '', []).calculateHostClass().calculateRenderRows();
  }

  public withLoader(loader: SolLoader): SolLoaderState {
    return this.copy({ loader }).calculateHostClass().calculateRenderRows();
  }

  public withLoading(loading: boolean): SolLoaderState {
    return this.copy({ loading }).calculateHostClass();
  }

  public withWaveIndex(waveIndex: number): SolLoaderState {
    return this.copy({ waveIndex }).calculateRenderRows();
  }

  private calculateHostClass(): SolLoaderState {
    const g = this.loader.gap;
    const hostClass = !this.loading ? 'sol-loader' : `sol-loader sol-loader-g-${g} sol-loader-px-${this.loader.margin}`;
    return this.copy({ hostClass });
  }

  private calculateRenderRows(): SolLoaderState {
    const renderRows: SolLoaderRenderRow[] = [];
    const g = this.loader.gap;
    const frozen = this.loader.frozen;
    let waveIndex = this.waveIndex;
    for (const loaderRow of this.loader.rows) {
      const w = loaderRow.width ?? '100';
      const rowClass = `sol-loader-row sol-loader-g-${g} sol-loader-w-${w}`;
      const cells: SolLoaderRenderCell[] = [];
      for (const loaderCell of loaderRow.cells) {
        const h = loaderCell.height ?? '32';
        const opacity = frozen ? SolLoaderState.frozenOpacity : SkeletonTools.nextWaveItem(waveIndex).value;

        if (!frozen) {
          waveIndex = SkeletonTools.bumpWaveIndex(waveIndex);
        }

        cells.push({
          cellClass: `sol-loader-cell sol-loader-h-${h}`,
          opacity,
        });
      }
      renderRows.push({ rowClass, cells });
    }
    return this.copy({ renderRows });
  }

  private copy(props: Partial<SolLoaderState>): SolLoaderState {
    return ObjectTools.copy<SolLoaderState>(this, props);
  }
}
